import { useQuery } from '@tanstack/react-query';
import { getListings } from '@/api/calls/listings';
import { HomeQCard } from '@/api/types/listings';
import {
  Box,
  Container,
  Grid,
  Card,
  Heading,
  Text,
  Image,
  Badge,
  Button,
  HStack,
  VStack,
  Link,
  Icon,
  Skeleton,
} from '@chakra-ui/react';
import { ExternalLink, MapPin, Home, Calendar, AlertTriangle } from 'lucide-react';
import { ContentCard } from '@/components/ContentCard';
import { SectionHeader } from '@/components/SectionHeader';

export default function AvailablePropertiesView() {
  const {
    data: listingsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['listings'],
    queryFn: () => getListings(),
    select: data => data.data,
  });

  if (isLoading) {
    return (
      <Box bg="bg.subtle" minH="100vh" py={{ base: '12', md: '20' }}>
        <Container maxW="7xl">
          <VStack gap={{ base: '8', md: '12' }} align="stretch">
            <SectionHeader
              eyebrow="Lediga bostäder"
              heading="Hittar tillgängliga bostäder..."
              description="Vänligen vänta medan vi laddar de senaste lediga bostäderna."
            />
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
              gap={6}
            >
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Card.Root key={i}>
                  <Skeleton height="200px" />
                  <Card.Header>
                    <Skeleton height="24px" width="80%" />
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={3} align="stretch">
                      <Skeleton height="20px" />
                      <Skeleton height="20px" />
                      <Skeleton height="36px" />
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>
    );
  }

  const PropertyCard: React.FC<{ property: HomeQCard }> = ({ property }) => {
    return (
      <Card.Root
        key={property.id}
        overflow="hidden"
        borderWidth="1px"
        borderColor="border"
        display="flex"
        flexDirection="column"
        _hover={{
          shadow: 'md',
          transform: 'translateY(-2px)',
          transition: 'all 0.2s',
        }}
      >
        {property.images.length > 0 && (
          <Box position="relative" overflow="hidden">
            <Image
              src={property.images[0].image}
              alt={property.title}
              h="240px"
              w="100%"
              objectFit="cover"
            />
            <Badge
              position="absolute"
              top="3"
              right="3"
              colorPalette="green"
              size="lg"
              px={3}
              py={1}
              fontWeight="bold"
            >
              {property.rent.toLocaleString()} kr/mån
            </Badge>
          </Box>
        )}
        <Card.Header pb={3}>
          <Heading size="md" lineClamp={2} fontWeight="semibold" letterSpacing="tight">
            {property.title}
          </Heading>
        </Card.Header>
        <Card.Body pt={0} display="flex" flexDirection="column" flex="1">
          <VStack align="stretch" gap={4} flex="1">
            <Box>
              <HStack gap={6} fontSize="sm" color="fg.muted" wrap="wrap">
                <HStack gap={1.5}>
                  <Icon color="blue.fg">
                    <Home size={18} />
                  </Icon>
                  <Text fontWeight="medium">{property.area} m²</Text>
                </HStack>
                <HStack gap={1.5}>
                  <Icon color="blue.fg">
                    <MapPin size={18} />
                  </Icon>
                  <Text fontWeight="medium">{property.city}</Text>
                </HStack>
                <HStack gap={1.5}>
                  <Icon color="blue.fg">
                    <Home size={18} />
                  </Icon>
                  <Text fontWeight="medium">{property.rooms} rum</Text>
                </HStack>
              </HStack>

              {property.date_access && (
                <HStack gap={1.5} fontSize="sm" color="fg.muted" mt={4}>
                  <Icon color="blue.fg">
                    <Calendar size={18} />
                  </Icon>
                  <Text fontWeight="medium">Tillträde: {property.date_access}</Text>
                </HStack>
              )}
            </Box>

            <Link
              href={`https://www.homeq.se${property.uri}`}
              target="_blank"
              rel="noopener noreferrer"
              _hover={{ textDecoration: 'none' }}
              mt="auto"
            >
              <Button colorPalette="blue" size="md" width="100%">
                <Icon>
                  <ExternalLink size={18} />
                </Icon>
                Visa detaljer
              </Button>
            </Link>
          </VStack>
        </Card.Body>
      </Card.Root>
    );
  };

  if (isError) {
    return (
      <Box bg="bg.subtle" minH="100vh" py={{ base: '12', md: '20' }}>
        <Container maxW="7xl">
          <VStack gap={{ base: '8', md: '12' }} align="stretch">
            <SectionHeader
              eyebrow="Lediga bostäder"
              heading="Något gick fel"
              description="Vi kunde inte ladda lediga bostäder just nu. Vänligen försök igen senare."
            />
            <Box
              p={6}
              borderRadius="xl"
              bg="red.subtle"
              borderWidth="1px"
              borderColor="red.muted"
              maxW="2xl"
              mx="auto"
            >
              <HStack gap={3}>
                <Icon color="red.fg" fontSize="2xl">
                  <AlertTriangle />
                </Icon>
                <VStack align="start" gap={1}>
                  <Text fontWeight="semibold" color="red.fg" fontSize="lg">
                    Fel vid laddning
                  </Text>
                  <Text color="red.fg" fontSize="sm">
                    Kunde inte hämta information om lediga bostäder. Försök igen senare eller
                    kontakta oss om problemet kvarstår.
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  const hasResults = listingsData?.results && listingsData.results.length > 0;

  return (
    <Box bg="bg.subtle" minH="100vh" py={{ base: '12', md: '20' }}>
      <Container maxW="7xl">
        <VStack gap={{ base: '8', md: '12' }} align="stretch">
          <SectionHeader
            eyebrow="Lediga bostäder"
            heading={
              !hasResults
                ? 'Inga lediga bostäder just nu'
                : `${listingsData?.total_hits || 0} lediga bostäder`
            }
            description={
              !hasResults
                ? 'Det finns för närvarande inga lediga bostäder tillgängliga, men nya objekt kan komma när som helst.'
                : 'Utforska våra tillgängliga bostäder och hitta ditt nästa hem.'
            }
          />

          {!hasResults ? (
            <ContentCard
              maxW="2xl"
              mx="auto"
              shadow=""
              border=""
              title="Lämna en intresseanmälan"
              description="Registrera ditt intresse så kontaktar vi dig när nya bostäder blir tillgängliga."
              icon={<Home />}
              action={{
                label: 'Lämna intresseanmälan',
                href: '/rental-application',
                variant: 'outline',
              }}
            />
          ) : (
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
              gap={6}
            >
              {listingsData.results.map((property: HomeQCard) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </Grid>
          )}
        </VStack>
      </Container>
    </Box>
  );
}
